import type { Passage } from "./types";

export const passage: Passage = {
  slug: "romans-8-28",
  reference: "Romans 8:28",
  title: "Romans 8:28: what all things work together for good means",
  h1: "Romans 8:28, and the two verses either side of it",
  description:
    "All things work together for good is said at funerals and it does not mean everything turns out fine. Verse 29 says what the good is, and it is not comfort.",
  targets: [
    "romans 8 28 meaning",
    "all things work together for good explained",
    "what does romans 8 28 mean",
  ],
  published: "2026-09-03",
  standfirst:
    "It is the verse people reach for when they do not know what to say, and it is the one most likely to make things worse if the sentence is stopped where the poster stops it.",
  verse: {
    text: "We know that all things work together for good for those who love God, for those who are called according to his purpose.",
    ref: "Romans 8:28",
  },
  context: [
    {
      text: "In the same way, the Spirit also helps our weaknesses, for we don't know how to pray as we ought. But the Spirit himself makes intercession for us with groanings which can't be uttered.",
      ref: "Romans 8:26",
    },
    {
      text: "For whom he foreknew, he also predestined to be conformed to the image of his Son, that he might be the firstborn among many brothers.",
      ref: "Romans 8:29",
    },
  ],
  takeaways: [
    "Verse 29 defines the good: being conformed to the image of the Son. It is a statement about what a person becomes, not about circumstances improving.",
    "Verse 26, two lines earlier, describes people who do not know how to pray and groanings that cannot be put into words. The chapter assumes the situation is bad.",
    "Work together for good is not the same as is good. Paul is not asking anybody to call the thing itself good, and the chapter's own vocabulary — suffering, groaning, futility — rules that out.",
    "Said too early to someone in the middle of something, it functions as a request that they stop being upset. That is a misuse of the verse rather than an application of it.",
  ],
  body: [
    {
      type: "h2",
      id: "the-chapter-it-sits-in",
      text: "The chapter it sits in",
    },
    {
      type: "p",
      text: "Romans 8 is the most quoted chapter Paul wrote and it is unusually frank. It contains creation groaning, believers groaning, the Spirit groaning, present sufferings, futility, and a list of things — famine, sword, persecution — that are considered as live possibilities rather than hypotheticals.",
    },
    {
      type: "p",
      text: "Verse 28 arrives inside all of that. It is not a claim made from a comfortable position about somebody else's difficulty; it is a claim made in the middle of a chapter that has already conceded everything.",
    },
    {
      type: "h2",
      id: "what-the-good-is",
      text: "What the good is",
    },
    {
      type: "p",
      text: "The next sentence answers it, and it is the sentence that almost never travels with the verse. The good is being conformed to the image of the Son — becoming a particular kind of person. Nothing in it promises that the circumstances resolve, that the illness lifts or that the loss is compensated.",
    },
    {
      type: "p",
      text: "That is a smaller promise in one direction and a much larger one in another. It cannot be falsified by a bad outcome, which is exactly why it can be said at a funeral without being a lie — and why it should usually not be said at one, out loud, to somebody who did not ask.",
    },
    {
      type: "h2",
      id: "the-grammar",
      text: "A note on the grammar",
    },
    {
      type: "p",
      text: "Some manuscripts and translations make God the subject: in all things God works for the good. Others keep all things as the subject, as here. The difference matters less than it looks — in both readings there is an agent, and the verse is not claiming that events arrange themselves.",
    },
    {
      type: "p",
      text: "What both readings exclude is the fatalistic version, in which whatever happened was for the best. Paul has spent the chapter calling the present state of things futility and groaning. He is not now saying it was fine all along.",
    },
  ],
  misreading: {
    claim:
      "That everything will turn out well — that the job loss leads to a better job, the illness to a lesson, the bereavement to a reason, and that a Christian should be able to see the good coming.",
    correction: [
      "This is the version said at hospital bedsides, and it is the version that drives people out of churches. It sets up an expectation the verse does not make, and when the expectation fails the reader concludes either that God did not keep his word or that their own faith was defective.",
      "The definition of the good is in the next verse and it is not circumstantial: conformed to the image of the Son. The claim is that nothing is wasted in that process, not that everything in the process is pleasant or that it will look explicable afterwards.",
      "There is also a question of who may say it. Paul writes it about himself and people like him, from inside a life of beatings and shipwrecks. Said by a sufferer it is defiance. Said to a sufferer by somebody comfortable, at the wrong moment, it is a request that they be quieter about their grief.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read 26 to 30 as one movement, not 28 alone. Verse 26 concedes that you do not know how to pray; verse 29 says what the good is. Between them the famous line stops being a slogan.",
    },
    {
      type: "p",
      text: "And in someone else's crisis, do not lead with it. If the words have gone entirely, {a prayer that admits that} is a better opening than a verse about outcomes.",
      links: {
        "a prayer that admits that": "/prayers/prayer-when-you-cant-pray",
      },
    },
    {
      type: "note",
      text: "There is a version of this verse that is worth keeping, and it is the one Paul is using. Held from inside a bad stretch, by the person in it, it is a refusal to concede that the stretch is pointless. That is not the same as claiming it is good, and the difference is audible: one is defiance, the other is tidying.",
    },
    {
      type: "note",
      text: "The test for whether you are using it well: could the person you are saying it to say it back to you, about their own situation, and mean it? If not, wait.",
    },
  ],
  faq: [
    {
      q: "What does Romans 8:28 actually promise?",
      a: "That nothing is wasted in the process of a person being conformed to the image of the Son — which verse 29 states as the definition of the good. It does not promise that circumstances improve or that the reason will become visible.",
    },
    {
      q: "Does Romans 8:28 mean everything happens for a reason?",
      a: "No. Paul spends the same chapter describing creation subjected to futility and groaning, and the Spirit praying in words that cannot be uttered. He is not saying the bad thing was secretly good.",
    },
    {
      q: "Should I say Romans 8:28 to someone who is grieving?",
      a: "Usually not, and not early. In Paul's mouth it is defiance said from inside suffering; said to a grieving person by someone comfortable it functions as a request to stop being upset.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so the verse is printed whole rather than referred to. It makes all things the subject — 'all things work together for good' — where several translations make God the one doing the working, which is the difference this page turns on.",
    },
  ],
  related: ["phone-addiction-bible-verses", "how-to-memorize-bible-verses"],
  relatedTopics: ["bible-verses-about-grief", "bible-verses-about-strength"],
  relatedPassages: ["jeremiah-29-11", "philippians-4-13"],
};
